'use client';

import { useQuery } from '@tanstack/react-query';
import { useLocale, useTranslations } from 'next-intl';

import Text from '@commons/components/Text';
import BaseContainer from '@commons/containers/BaseContainer';
import Flex from '@commons/containers/Flex';
import PageBuilder from '@commons/pagebuilder';
import type { DrupalInsightNodeProps } from '@commons/pagebuilder/views/Insights';
import { fetchView } from '@commons/services/view';

import styles from './Insights.module.scss';

/**
 * Type definition of the shared member profile insights component properties.
 */

export type MemberProfileInsightsProps = {
  id: number;
  viewId: string;
  display: string;
  title?: string;
};

/**
 * MemberProfileInsights component.
 *
 * Fetches and renders a set of insights based on the member id.
 */

const MemberProfileInsights = ({ id, viewId, display, title }: MemberProfileInsightsProps) => {
  const t = useTranslations('nodes.member');
  const locale = useLocale();

  const { data } = useQuery({
    queryKey: [viewId, display, locale, id],
    queryFn: () =>
      fetchView<DrupalInsightNodeProps>({
        id: viewId,
        display,
        locale,
        argument: [id.toString()],
      }),
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  if (data && data.length > 0) {
    const renderedContent = data.map(PageBuilder.renderNode);

    return (
      <BaseContainer width={12}>
        <Flex align="center" border="bottom" paddingTop="md" paddingBottom="md" marginBottom="xs">
          <Text level="label-lg" transform="uppercase" marginBottom="none">
            {title || t('publishedReports')}
          </Text>
        </Flex>
        <Flex gap="xs" direction="column" className={styles.insights}>
          {renderedContent}
        </Flex>
      </BaseContainer>
    );
  }

  return null;
};

export default MemberProfileInsights;
